import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { useActionHook } from "hooks";
import { debounce, pickBy } from "lodash";
import { useReportsActions, useReportsQueriesSelector } from "store";
import {
  CashflowGraph,
  ReportAllocationTable,
  ReportCashflowAllocationTarget,
  ReportCashflowAum,
  ReportPredictionTable
} from "store/types";

import { AllocationTable } from "./Allocation-table/AllocationTable";
import { ManageBlock } from "./Prediction-table/ManageBlock";
import { PredictionTable } from "./Prediction-table/PredictionTable";
import { TableTabs } from "./TableTabs";

interface Props {
  getGraph: (set_id: number) => Promise<CashflowGraph>;
  allocationTargetData: ReportCashflowAllocationTarget[] | null;
  aumData: ReportCashflowAum | null;
  allocationTableData?: ReportAllocationTable;
  allocationTableLoading: boolean;
}

export const TablesWrapper: React.FC<Props> = ({
  getGraph,
  allocationTargetData,
  aumData,
  allocationTableData,
  allocationTableLoading
}) => {
  const { getCashflowPresetsAction, getCashflowTableAction, downloadCashflowTableAction, getCashflowSetsAction } =
    useReportsActions();
  const { currency, exchangeMethod, dateRange, filters, use_accounting_date, cashflowSetId } =
    useReportsQueriesSelector();

  const memoizedFilters = useMemo(() => pickBy(filters, (value) => value.length > 0), [filters]);

  const [currentTab, setCurrentTab] = useState<"prediction" | "allocation">("prediction");

  const { action: actionPresets, data: presets = [] } = useActionHook(getCashflowPresetsAction);
  const { action: fetchSets, data: sets = [] } = useActionHook(getCashflowSetsAction);

  const isDefaultSet = useMemo(() => {
    if (!cashflowSetId || !sets.length) return true;
    const currentSet = sets.find((set) => set.id === cashflowSetId);
    return currentSet?.user_id === null;
  }, [cashflowSetId, sets]);

  const {
    data: predictionTableData,
    loading: predictionTableLoading,
    action: getPredictionTableData,
    setData: setPredictionTableData
  } = useActionHook(getCashflowTableAction, {
    defaultLoadingState: true
  });

  // Debounced version of the API call to prevent duplicate requests
  const debouncedGetPredictionTableData = useMemo(() => debounce(getPredictionTableData, 300), []);

  const downloadCashflowTable = useCallback(async () => {
    if (!cashflowSetId) return;
    await downloadCashflowTableAction({
      set_id: cashflowSetId,
      currency,
      dateRange,
      exchangeDate: exchangeMethod,
      filters: memoizedFilters
    });
  }, [cashflowSetId, currency, dateRange, exchangeMethod, memoizedFilters]);

  useEffect(() => {
    if (!cashflowSetId) return;
    debouncedGetPredictionTableData({
      set_id: cashflowSetId,
      currency,
      dateRange,
      exchangeDate: exchangeMethod,
      use_accounting_date,
      filters: memoizedFilters
    });
  }, [
    cashflowSetId,
    currency,
    dateRange,
    exchangeMethod,
    use_accounting_date,
    memoizedFilters,
    debouncedGetPredictionTableData
  ]);

  useEffect(() => {
    actionPresets();
    fetchSets();
  }, []);

  useEffect(() => {
    if (!allocationTargetData?.length) {
      setCurrentTab("prediction");
    }
  }, [allocationTargetData]);

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
        p: "16px",
        gap: "16px",
        height: "100%"
      }}
    >
      <ManageBlock
        downloadCashflowTable={downloadCashflowTable}
        showDownload={currentTab === "prediction" && Boolean(cashflowSetId)}
      />
      {Boolean(allocationTargetData?.length) && <TableTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />}
      {currentTab === "prediction" ? (
        <PredictionTable
          //added key to force re-render when predictionTableLoading
          key={`prediction-table-${predictionTableLoading}`}
          getGraph={getGraph}
          currentSetId={cashflowSetId}
          presets={presets}
          predictionTableData={predictionTableData as ReportPredictionTable}
          predictionTableLoading={predictionTableLoading}
          setPredictionTableData={
            setPredictionTableData as React.Dispatch<React.SetStateAction<ReportPredictionTable | null>>
          }
          isDefaultSet={isDefaultSet}
        />
      ) : (
        <AllocationTable
          allocationTargetData={allocationTargetData}
          aumData={aumData}
          allocationTableData={allocationTableData as ReportAllocationTable}
          allocationTableLoading={allocationTableLoading}
        />
      )}
    </Stack>
  );
};
